import { useMemo, useState } from "react";
import { PaintBucket, Search } from "lucide-react";
import { Pill, Segmented, TextInput, TH, TD } from "../../../../components/ui";
import Toggle from "../../../../../components/ui/Toggle";
import NoDataState from "../../../../../components/NoDataState";
import SectionShell from "../SectionShell";
import useSectionDraft from "../useSectionDraft";
import { CellNumber, CellText } from "../cells";

const PRICE_COLS = [
  { key: "pricePerQt", label: "$/Qt" },
  { key: "pricePerGal", label: "$/Gal" },
  { key: "pricePer5Gal", label: "$/5-gal" },
];

export default function ProductsSection({ config, readOnly, onSaved }) {
  const draft = useSectionDraft(config, "products", onSaved, "Products");
  const products = draft.value;
  const [search, setSearch] = useState("");
  const [maker, setMaker] = useState("ALL");

  const makers = useMemo(() => [...new Set((products || []).map((p) => p.manufacturer))], [products]);
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (products || [])
      .map((p, index) => ({ p, index }))
      .filter(({ p }) => (maker === "ALL" || p.manufacturer === maker) && (!q || [p.id, p.line, p.sheen].some((v) => v.toLowerCase().includes(q))));
  }, [products, search, maker]);

  if (!products) return null;
  const set = (index, key) => (v) => draft.update((d) => { d[index][key] = v; });
  const activeCount = products.filter((p) => p.active !== false).length;
  const nameOf = (id) => products.find((p) => p.id === id)?.line;

  return (
    <SectionShell
      title="Products"
      subtitle={`${activeCount} of ${products.length} products active. Inactive products are hidden from estimate dropdowns.`}
      icon={PaintBucket}
      draft={draft}
      readOnly={readOnly}
    >
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative w-full max-w-xs">
          <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-text_muted" aria-hidden="true" />
          <TextInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search id, line, sheen…" className="pl-8" aria-label="Search products" />
        </div>
        <Segmented
          ariaLabel="Filter by manufacturer"
          value={maker}
          onChange={setMaker}
          options={[{ value: "ALL", label: "All", count: products.length }, ...makers.map((m) => ({ value: m, label: m, count: products.filter((p) => p.manufacturer === m).length }))]}
        />
      </div>

      {rows.length === 0 ? (
        <NoDataState icon="search" title="No products match" message="Try a different search term or manufacturer." showBack={false} showRefresh={false} size="small" />
      ) : (
        <div className="max-h-[calc(100vh-330px)] overflow-auto rounded-[4px] border border-border">
          <table className="w-full border-separate border-spacing-0">
            <thead className="sticky top-0 z-20">
              <tr>
                <th className={TH}>Product ID</th>
                <th className={TH}>Manufacturer</th>
                <th className={TH}>Line</th>
                <th className={TH}>Sheen</th>
                <th className={TH}>Type</th>
                <th className={`${TH} text-right`}>Coverage SF/gal</th>
                {PRICE_COLS.map((c) => (
                  <th key={c.key} className={`${TH} text-right`}>
                    {c.label}
                  </th>
                ))}
                <th className={TH}>Equivalent</th>
                <th className={TH}>Active</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ p, index }) => (
                <tr key={p.id} className={p.active === false ? "opacity-60" : ""}>
                  <td className={`${TD} font-mono text-[11.5px]`}>{p.id}</td>
                  <td className={`${TD} text-text_secondary`}>{p.manufacturer}</td>
                  <td className={`${TD} min-w-[220px] py-1`}>
                    <CellText value={p.line} onChange={set(index, "line")} disabled={readOnly} ariaLabel={`${p.id} line`} />
                  </td>
                  <td className={`${TD} w-32 py-1`}>
                    <CellText value={p.sheen} onChange={set(index, "sheen")} disabled={readOnly} ariaLabel={`${p.id} sheen`} />
                  </td>
                  <td className={TD}>
                    <Pill tone={p.type === "PRIMER" ? "info" : "primary"}>{p.type === "PRIMER" ? "Primer" : "Finish"}</Pill>
                  </td>
                  <td className={`${TD} w-28 py-1`}>
                    <CellNumber value={p.coverage} onChange={set(index, "coverage")} disabled={readOnly} step="5" min={1} ariaLabel={`${p.id} coverage`} />
                  </td>
                  {PRICE_COLS.map((c) => (
                    <td key={c.key} className={`${TD} w-28 py-1`}>
                      <CellNumber value={p[c.key]} onChange={set(index, c.key)} disabled={readOnly} step="0.01" min={0} suffix="$" ariaLabel={`${p.id} ${c.label}`} />
                    </td>
                  ))}
                  <td className={`${TD} text-[12px] text-text_secondary`}>{p.equivalentId ? nameOf(p.equivalentId) || p.equivalentId : "—"}</td>
                  <td className={TD}>
                    <label className="inline-flex">
                      <span className="sr-only">{p.id} active</span>
                      <Toggle size="sm" checked={p.active !== false} onChange={set(index, "active")} disabled={readOnly} />
                    </label>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionShell>
  );
}
